import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  FEATURE_FLAGS,
  type FeatureFlagKey,
  type FeatureFlagValues,
  hasVariants,
  getVariants
} from "@/constants/featureFlags";

type FlagValue = boolean | string;

const mergeFlags = (
  configFlags: Record<string, FlagValue>,
  localOverrides: Record<string, FlagValue>
): FeatureFlagValues => {
  const result: Record<string, FlagValue> = {};

  for (const [key, flag] of Object.entries(FEATURE_FLAGS)) {
    if (localOverrides[key] !== undefined) {
      result[key] = localOverrides[key];
    } else if (configFlags[key] !== undefined) {
      result[key] = configFlags[key];
    } else {
      result[key] = flag.defaultValue;
    }
  }

  return result as FeatureFlagValues;
};

const detectWarnings = (configFlags: Record<string, FlagValue>): string[] => {
  const constantKeys = Object.keys(FEATURE_FLAGS);

  return Object.keys(configFlags).filter(key => !constantKeys.includes(key));
};

type FlagSource = "override" | "config" | "default";

type FeatureFlagsStore = {
  flags: FeatureFlagValues;
  configFlags: Record<string, FlagValue>;
  localOverrides: Record<string, FlagValue>;
  warnings: string[];

  initializeFromConfig: (configFlags: Record<string, FlagValue>) => void;

  toggleFlag: (key: FeatureFlagKey) => void;
  setFlag: (key: FeatureFlagKey, value: FlagValue) => void;
  setVariant: (key: FeatureFlagKey, variant: string) => void;
  resetFlags: () => void;
  isEnabled: (key: FeatureFlagKey) => boolean;
  getValue: (key: FeatureFlagKey) => FlagValue;

  clearOverride: (key: FeatureFlagKey) => void;
  clearAllOverrides: () => void;
  getSource: (key: FeatureFlagKey) => FlagSource;
};

const useFeatureFlagsStore = create<FeatureFlagsStore>()(
  persist(
    (set, get) => ({
      flags: {} as FeatureFlagValues,
      configFlags: {},
      localOverrides: {},
      warnings: [],

      initializeFromConfig: (configFlags: Record<string, FlagValue>) => {
        const state = get();
        const warnings = detectWarnings(configFlags);
        const merged = mergeFlags(configFlags, state.localOverrides);

        set({
          configFlags,
          warnings,
          flags: merged
        });
      },

      toggleFlag: (key: FeatureFlagKey) => {
        const state = get();
        const currentValue = state.flags[key];

        let newValue: FlagValue;

        if (hasVariants(key)) {
          if (currentValue === false) {
            const variants = getVariants(key);
            newValue = variants[0] || false;
          } else {
            newValue = false;
          }
        } else {
          newValue = !currentValue;
        }

        const newOverrides = {
          ...state.localOverrides,
          [key]: newValue
        };

        const merged = mergeFlags(state.configFlags, newOverrides);

        set({
          localOverrides: newOverrides,
          flags: merged
        });
      },

      setFlag: (key: FeatureFlagKey, value: FlagValue) => {
        const state = get();

        const newOverrides = {
          ...state.localOverrides,
          [key]: value
        };

        const merged = mergeFlags(state.configFlags, newOverrides);

        set({
          localOverrides: newOverrides,
          flags: merged
        });
      },

      setVariant: (key: FeatureFlagKey, variant: string) => {
        const state = get();

        const newOverrides = {
          ...state.localOverrides,
          [key]: variant
        };

        const merged = mergeFlags(state.configFlags, newOverrides);

        set({
          localOverrides: newOverrides,
          flags: merged
        });
      },

      resetFlags: () => {
        const state = get();

        const merged = mergeFlags(state.configFlags, {});

        set({
          localOverrides: {},
          flags: merged
        });
      },

      isEnabled: (key: FeatureFlagKey) => {
        const value = get().flags[key];

        return value !== false && value !== undefined;
      },

      getValue: (key: FeatureFlagKey) => {
        return get().flags[key];
      },

      clearOverride: (key: FeatureFlagKey) => {
        const state = get();
        const newOverrides = { ...state.localOverrides };

        delete newOverrides[key];

        const merged = mergeFlags(state.configFlags, newOverrides);

        set({
          localOverrides: newOverrides,
          flags: merged
        });
      },

      clearAllOverrides: () => {
        const state = get();

        const merged = mergeFlags(state.configFlags, {});

        set({
          localOverrides: {},
          flags: merged
        });
      },

      getSource: (key: FeatureFlagKey): FlagSource => {
        const state = get();

        if (state.localOverrides[key] !== undefined) {
          return "override";
        }

        if (state.configFlags[key] !== undefined) {
          return "config";
        }

        return "default";
      }
    }),
    {
      name: "tigim-feature-flags",
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        localOverrides: state.localOverrides
      })
    }
  )
);

export default useFeatureFlagsStore;
export type { FlagSource, FlagValue };
